/**
 * 手動イベントの最小セット定義（GF-1888 / GF-1896）。
 *
 * ⚠️ Autocapture 主軸の運用方針：
 *   - クリック / ページ遷移 / フォーム submit は Autocapture に任せ、ここに追加しない。
 *   - ここに定義するのは「Autocapture で取れない / 信頼性が必要なドメインイベント」だけ。
 *   - 新規追加は docs（posthog.md）の承認フローに従うこと。「念のため」の追加は禁止。
 *
 * イベント名（EVENT_NAMES の値）は PostHog のイベント識別子として snake_case、プロパティキーは GA4のプロパティに合わせて camelCase で定義する。
 */
export const EVENT_NAMES = {
  /** 購入完了。金額・商品ID・注文ID は Autocapture では取れないため手動送信する。 */
  purchaseCompleted: 'purchase_completed',
  /**
   * 購入明細（1商品=1件）。経路別の商品分析（構成比・経路別アイテム収益）を明細スカラーで行うため、
   * purchase_completed（注文スコープ）とは別に明細スコープで手動送信する。
   */
  purchaseItem: 'purchase_item',
  /** 新規会員登録完了。登録の確定は API 成功でしか判定できないため手動送信する（購入と並ぶ CV イベント）。 */
  signupCompleted: 'signup_completed',
  /** デザインツールのキャンバス変形（移動/拡大/回転）。Fabric.js の canvas は DOM クリックでないため Autocapture で取れず手動送信する（GF-2121）。 */
  designCanvasTransform: 'design_tool_canvas_transform',
  /** デザインツールで素材をキャンバスに配置（=デザイン着手の正式シグナル）。canvas 処理のため Autocapture で取れず手動送信する（GF-2121）。 */
  designMaterialPlaced: 'design_tool_material_placed',
  /** デザインツール離脱サマリ。離脱位置(exit_phase)・滞在時間・着手有無を1セッション1回だけ送る。即離脱と諦め離脱の切り分け用（GF-2121）。 */
  designToolExit: 'design_tool_exit',
  /** カート投入。GA4 の add_to_cart と同時に送り、経路別ファネル（デザイン完了など）を PostHog で組むために手動送信する。 */
  addToCart: 'add_to_cart',
  /** コマクリック。作品別のコマ関心度を見る。 */
  materialClick: 'material_click',
  /** TOPピックアップバナークリック（集英社要望バナーの効果測定）。 */
  pickupBannerClick: 'pickup_banner_click',
  /** TOPヒーロー探索導線クリック（入口別の下流成果を見る）。 */
  exploreEntryClick: 'explore_entry_click',
} as const;

export type EventName = (typeof EVENT_NAMES)[keyof typeof EVENT_NAMES];

/**
 * 各手動イベントのプロパティスキーマ。キーは {@link EVENT_NAMES} の「値」。
 * PII（メール / 住所 / 電話 / カード番号）は絶対に含めない。
 */
export type EventProperties = {
  purchase_completed: {
    orderId: string;
    /** 税込合計金額 */
    amount: number;
    productIds: string[];
    /** 購入商品の合計点数 */
    quantity: number;
    /** 'credit' | 'paypay' 等の決済手段 */
    paymentMethod: string;
    /** 会員購入かゲスト購入か */
    isGuest: boolean;
    // 経路（cartRoute 等）は明細スコープのため purchase_item 側に持たせ、注文スコープのここには載せない
    /** 注文内の distinct な作品 ID */
    workIds: number[];
  };
  purchase_item: {
    /** 注文スコープの purchase_completed と突き合わせるための注文ID */
    orderId: string;
    /** 商品識別子（SKU コード）。商品別集計のブレイクダウンキー */
    sku: string;
    /** 商品名（例: スタンダードTシャツ）。売れ筋商品の可読ラベル */
    name: string;
    /** カラー */
    color: string;
    /** サイズ（template 商品などサイズが無い場合は空文字） */
    size: string;
    /** 商品タイプ（design_tool / template 等） */
    productType: string;
    /** この明細の小計 */
    amount: number;
    /** この明細の購入点数 */
    quantity: number;
    // 経路は明細スカラーで持ち、そのまま breakdown する（配列にしない）。解決不能時は 'unknown'
    /** この明細の cartRoute（最終経路） */
    cartRoute: string;
    /** この明細の entryRoute（元経路） */
    entryRoute: string;
    /** みんなのデザイン起点なら true */
    basedOnUserDesign: boolean;
    /** シェアURL流入なら true */
    fromShareUrl: boolean;
    /** 作品名 */
    workName: string;
    /** 印刷エリア名の配列（例: ['前面', '背面']）。どの面に作成したかの分析用 */
    prints: string[];
    /** 作品 ID（解決できた明細のみ付与） */
    workId?: number;
  };
  /** 登録情報（メール・氏名等）は PII のためプロパティは送らない */
  signup_completed: Record<string, never>;
  design_tool_canvas_transform: {
    /** 直前の変形種別。拡大と縮小は分けて記録する */
    transformType: 'move' | 'scale_up' | 'scale_down' | 'rotate';
    productId: number;
    partId: number;
    /** canvas 上のコマに紐づく素材ID */
    materialId: number;
    /** 作品ID（作品別分析用） */
    workId?: number;
    user_id: number;
  };
  design_tool_material_placed: {
    productId: number;
    partId: number;
    materialId: number;
    workId?: number;
    user_id: number;
  };
  design_tool_exit: {
    /** 離脱時点で到達していた最深フェーズ */
    exitPhase: 'init' | 'before_first_action' | 'editing' | 'preview' | 'to_cart';
    /** 離脱までに一度でもコマを配置したか（削除で0になっても true 維持） */
    hasAddedMaterial: boolean;
    /** /design 到達〜離脱までの滞在ms（performance.now 差分） */
    dwellTimeMs: number;
    workId?: number;
    user_id: number;
    /** 前回セッションの未送信を localStorage から救出して送ったか（モバイルkill等の取りこぼし回収） */
    isRecovered?: boolean;
  };
  add_to_cart: {
    /** price * quantity の合計 */
    value: number;
    productIds: string[];
    /** カート投入点数の合計 */
    quantity: number;
    /** 会員投入かゲスト投入か */
    isGuest: boolean;
    /** cartRoute（"designed" 等）。経路別ファネルの起点にする */
    cartRoute: string;
    /** entryRoute（元経路） */
    entryRoute: string;
    /** みんなのデザイン起点なら true */
    basedOnUserDesign: boolean;
    /** シェアURL流入なら true */
    fromShareUrl: boolean;
    /** 作品ID（作品別のカート投入→購入ファネル分析用。解決できた場合のみ付与） */
    workId?: number;
    /** 作品名（解決できた場合のみ付与） */
    workName?: string;
  };
  material_click: {
    materialId: number;
    materialName: string;
    /** 作品ID（作品別のコマ関心度分析用） */
    workId: number;
    workName: string;
    user_id: number;
  };
  pickup_banner_click: {
    bannerId: number;
    /** 掲載枠の並び順（0始まり） */
    position: number;
    user_id: number;
  };
  explore_entry_click: {
    /** どの探索導線から入ったか */
    exploreMode: 'works' | 'userDesigns';
    user_id: number;
  };
};
